/**
 * src/services/weekly.api.ts 의 짝.
 *
 * Weekly 는 만들어 두지 않는다 — 부를 때마다 db.dailies 에서 모은다. 보는 주의 앞 주(지난주)는 제출한
 * Daily 만 세고, 보는 주는 계획(진행 예정)과 단계 일정만 읽는다. 전 직원이 본다. 사람별 날짜별 원본(days)은
 * 남의 Daily 원문이라 서버처럼 관리자에게만 채운다.
 *
 * 지난주 AI 글(story)은 ../data/weeklyStory 가 문장틀로 흉내 낸다. 코멘트는 db.weeklyComments 에 쌓인다 —
 * 쓴 사람이나 관리자만 지운다.
 */
import { HttpResponse, http } from "msw";
import { shiftDate, weekStartOf } from "@/lib/dates";
import type {
  Weekly,
  WeeklyComment,
  WeeklyCommentInput,
  WeeklyEvent,
  WeeklyFinance,
  WeeklyMember,
  WeeklyProject,
  WeeklyStory,
} from "@/types";
import type { MockDaily } from "../data/dailies";
import { db } from "../db";
import { buildWeeklyStory } from "../data/weeklyStory";
import { fail, latency, requireUser } from "../http";
import { HOURLY_COST, moneyOf, phaseState } from "./projects";

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
const round = (value: number) => Math.round(value * 100) / 100;

const between = (daily: MockDaily, from: string, to: string) =>
  daily.work_date >= from && daily.work_date <= to;
const submitted = (from: string, to: string) =>
  db.dailies.filter((daily) => between(daily, from, to));

const usageOf = (projectId: string) =>
  db.projectDetails.find((row) => row.project_id === projectId && row.field_id === "field-usage")
    ?.value_text ?? "";

const mergeLines = (texts: string[]) => [
  ...new Set(texts.flatMap((text) => text.split("\n").map((line) => line.trim())).filter(Boolean)),
];

function hoursOf(dailies: MockDaily[]) {
  const regular = round(sum(dailies.map((d) => d.regular_hours)));
  const overtime = round(sum(dailies.map((d) => d.overtime_hours)));
  return { regular, overtime, total: round(regular + overtime) };
}

function lineOf(lines: Map<string, WeeklyProject>, projectId: string, today: string) {
  const existing = lines.get(projectId);
  if (existing) return existing;
  const project = db.projects.find((p) => p.id === projectId);
  const open = db.projectPhases
    .filter((phase) => phase.project_id === projectId && phase.done_date === null)
    .sort((a, b) => a.sort_order - b.sort_order);
  const phase = open.find((p) => p.start_date !== null) ?? open[0];
  const line: WeeklyProject = {
    project_id: projectId,
    code: project?.code ?? null,
    name: project?.name ?? "",
    color: project?.color ?? "",
    usage: usageOf(projectId),
    status: project?.status ?? "active",
    phase: phase
      ? { name: phase.name, status: phaseState(phase, today).status, due_date: phase.due_date }
      : null,
    hours: 0,
    plan_hours: 0,
    plan_achievement: null,
    members: [],
    plans: [],
    images: [],
  };
  lines.set(projectId, line);
  return line;
}

/**
 * 재무 요약 — 관리자에게만. 인건비는 제출한 Daily 시간 × 시간단가(프로젝트 재무 탭과 같은 셈). 프로젝트마다
 * 지난주 인건비와 지금까지의 인건비 · 계약 금액 · 받은 돈(재무 탭의 요약)을 붙인다.
 */
function financeOf(
  last: MockDaily[],
  previous: MockDaily[],
  projects: WeeklyProject[],
  story: WeeklyStory | null
): WeeklyFinance {
  const cost = (hours: number) => Math.floor(hours * HOURLY_COST);
  const hoursOfRows = (dailies: MockDaily[], pick: (projectId: string | null) => boolean) =>
    sum(
      dailies.flatMap((d) => d.rows.filter((r) => pick(r.project_id)).map((r) => r.actual_hours))
    );
  const people = new Map<string, number>();
  for (const daily of last)
    people.set(
      daily.user_id,
      (people.get(daily.user_id) ?? 0) + sum(daily.rows.map((r) => r.actual_hours))
    );
  const base = {
    labor_cost: cost(hoursOfRows(last, () => true)),
    previous_labor_cost: cost(hoursOfRows(previous, () => true)),
    non_project_labor_cost: cost(hoursOfRows(last, (id) => !id)),
    projects: projects.map((project) => {
      const money = moneyOf(project.project_id).summary;
      return {
        project_id: project.project_id,
        labor_cost: cost(project.hours),
        total_labor_cost: money.labor_cost,
        contract_amount: money.contract_amount,
        received: money.received,
      };
    }),
    people: [...people].map(([user_id, hours]) => ({ user_id, labor_cost: cost(hours) })),
  };
  return { ...base, story: financeStory(story, base) };
}

const won = (amount: number) => `${amount.toLocaleString("ko-KR")}원`;

/**
 * 재무를 넣어 따로 쓴 AI 글 — 목에서는 일반 글에 돈 이야기 문장을 덧붙여 만든다. 실제로는 AI 가 Daily 와
 * 재무 숫자를 함께 읽고 따로 쓴다.
 */
function financeStory(story: WeeklyStory | null, finance: Omit<WeeklyFinance, "story">) {
  if (!story) return null;
  const diff = finance.labor_cost - finance.previous_labor_cost;
  const ratioOf = (id: string) => {
    const money = finance.projects.find((p) => p.project_id === id);
    return money?.contract_amount ? money.total_labor_cost / money.contract_amount : null;
  };
  const heavy = finance.projects
    .map((p) => ({ ...p, ratio: ratioOf(p.project_id) }))
    .filter((p) => p.ratio !== null && p.ratio >= 0.8);
  return {
    ...story,
    summary: [
      story.summary,
      `지난주 인건비는 ${won(finance.labor_cost)}으로 전주보다 ${won(Math.abs(diff))} ${diff >= 0 ? "늘었다" : "줄었다"}.`,
    ].join(" "),
    sections: story.sections.map((section) => {
      const money = finance.projects.find((p) => p.project_id === section.project_id);
      if (!money) return section;
      const ratio = ratioOf(section.project_id);
      // 돈 이야기는 한 문장으로 묶어 둘째 줄에 — 요약 칸은 세 줄까지만 보인다.
      const sentences = section.body.split(/(?<=다\.)\s+/);
      const moneyLine =
        ratio !== null
          ? `인건비 ${won(money.labor_cost)}이 들어 누적 ${won(money.total_labor_cost)}, 계약의 ${Math.round(ratio * 100)}%다.`
          : `인건비 ${won(money.labor_cost)}이 들어 누적 ${won(money.total_labor_cost)}이다. 아직 계약 금액이 없다.`;
      return {
        ...section,
        body: [sentences[0], moneyLine, ...sentences.slice(1)].join(" "),
      };
    }),
    watch: [
      ...story.watch,
      ...heavy.map((p) => {
        const name = projectName(p.project_id);
        return `${name}의 누적 인건비가 계약의 ${Math.round(p.ratio! * 100)}%다.`;
      }),
    ],
  };
}

const projectName = (id: string) => db.projects.find((p) => p.id === id)?.name ?? id;

function buildWeekly(weekOf: string, withDays: boolean): Weekly {
  const weekStart = weekStartOf(weekOf);
  const weekEnd = shiftDate(weekStart, 6);
  const lastStart = shiftDate(weekStart, -7);
  const lastEnd = shiftDate(weekStart, -1);
  const today = new Date().toISOString().slice(0, 10);
  const last = submitted(lastStart, lastEnd);
  const lines = new Map<string, WeeklyProject>();

  for (const daily of [...last].sort((a, b) => a.work_date.localeCompare(b.work_date))) {
    const name = db.users.find((u) => u.id === daily.user_id)?.name ?? daily.user_id;
    for (const row of daily.rows) {
      if (!row.project_id) continue;
      const line = lineOf(lines, row.project_id, today);
      let member: WeeklyMember | undefined = line.members.find((m) => m.user_id === daily.user_id);
      if (!member) {
        member = { user_id: daily.user_id, user_name: name, hours: 0, descriptions: [], days: [] };
        line.members.push(member);
      }
      member.hours = round(member.hours + row.actual_hours);
      member.descriptions.push(row.description);
      if (withDays)
        member.days.push({
          work_date: daily.work_date,
          hours: row.actual_hours,
          description: row.description,
        });
      line.hours = round(line.hours + row.actual_hours);
      line.plan_hours = round(line.plan_hours + row.plan_hours);
    }
  }
  // 지난주 이 프로젝트의 Daily 에 올린 사진 — 관리자만(남의 Daily 원본).
  if (withDays)
    for (const daily of last)
      for (const image of db.dailyImages.filter((row) => row.daily_table_id === daily.id)) {
        const line = image.project_id ? lines.get(image.project_id) : undefined;
        if (!line) continue;
        line.images.push({
          id: image.id,
          file_name: image.file_name,
          work_date: daily.work_date,
          user_name: db.users.find((u) => u.id === daily.user_id)?.name ?? daily.user_id,
        });
      }
  for (const line of lines.values()) {
    line.images.sort((a, b) => a.work_date.localeCompare(b.work_date));
    line.plan_achievement = line.plan_hours ? round(line.hours / line.plan_hours) : null;
    line.members.sort((a, b) => a.user_name.localeCompare(b.user_name, "ko"));
    for (const member of line.members) member.descriptions = mergeLines(member.descriptions);
  }

  // 이번 주 계획 — 제출 전이어도 계획은 계획이다.
  const plans = new Map<string, string[]>();
  for (const daily of db.dailies.filter((d) => between(d, weekStart, weekEnd))) {
    for (const row of daily.rows) {
      if (!row.project_id || !row.plan_description) continue;
      lineOf(lines, row.project_id, today);
      plans.set(row.project_id, [...(plans.get(row.project_id) ?? []), row.plan_description]);
    }
  }
  for (const [projectId, texts] of plans) lines.get(projectId)!.plans = mergeLines(texts);

  const events: WeeklyEvent[] = db.projectPhases.flatMap((phase) => {
    const project = db.projects.find((p) => p.id === phase.project_id);
    if (!project) return [];
    const dates = [
      ["start", phase.start_date],
      ["due", phase.due_date],
      ["done", phase.done_date],
    ] as const;
    return dates
      .filter(([, day]) => day !== null && day >= weekStart && day <= weekEnd)
      .map(([kind, day]) => ({
        project_id: project.id,
        code: project.code,
        name: project.name,
        color: project.color,
        phase_name: phase.name,
        kind,
        day: day!,
      }));
  });

  const projects = [...lines.values()].sort(
    (a, b) =>
      Number(a.code === null) - Number(b.code === null) ||
      (a.code ?? "").localeCompare(b.code ?? "") ||
      a.name.localeCompare(b.name, "ko")
  );
  const planHours = sum(projects.map((p) => p.plan_hours));
  const nonProjectRows = last.flatMap((daily) =>
    daily.rows.filter((row) => !row.project_id).map((row) => ({ ...row, user_id: daily.user_id }))
  );
  const previousDailies = submitted(shiftDate(lastStart, -7), shiftDate(lastStart, -1));
  const previous = hoursOf(previousDailies);
  const inLastWeek = (day: string | null) => day !== null && day >= lastStart && day <= lastEnd;
  return {
    week_start: weekStart,
    last_week_start: lastStart,
    hours: {
      ...hoursOf(last),
      previous_total: previous.total,
      previous_overtime: previous.overtime,
    },
    plan_achievement: planHours ? round(sum(projects.map((p) => p.hours)) / planHours) : null,
    milestones_done: db.projectPhases.filter((p) => inLastWeek(p.done_date)).length,
    milestones_due: db.projectPhases.filter((p) => inLastWeek(p.due_date)).length,
    projects,
    non_project: db.dailyCategories
      .map((category) => {
        const rows = nonProjectRows.filter((r) => r.category_id === category.id);
        return {
          category_id: category.id,
          name: category.name,
          hours: round(sum(rows.map((r) => r.actual_hours))),
          plan_hours: round(sum(rows.map((r) => r.plan_hours))),
          members: [...new Set(rows.filter((r) => r.actual_hours > 0).map((r) => r.user_id))]
            .map((id) => ({
              user_id: id,
              user_name: db.users.find((u) => u.id === id)?.name ?? id,
            }))
            .sort((a, b) => a.user_name.localeCompare(b.user_name, "ko")),
        };
      })
      .filter((row) => row.hours > 0),
    events: events.sort((a, b) => a.day.localeCompare(b.day)),
    story: buildWeeklyStory(db, lastStart),
    comments: db.weeklyComments.filter((c) => c.week_start === lastStart),
    // 관리자가 아니면 키 자체를 뺀다 — 서버가 금액을 떨어뜨리는 것과 같다.
    ...(withDays
      ? { finance: financeOf(last, previousDailies, projects, buildWeeklyStory(db, lastStart)) }
      : {}),
  };
}

export const weeklyHandlers = [
  http.get("/api/v1/weekly/", async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const weekOf = new URL(request.url).searchParams.get("week_of") ?? "";
    return HttpResponse.json(buildWeekly(weekOf, user.role === "admin"));
  }),

  http.post("/api/v1/weekly/comments/", async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as WeeklyCommentInput;
    const body = input.body?.trim();
    if (!body) return fail(422, "코멘트를 입력해 주세요.");
    if (!db.projects.some((p) => p.id === input.project_id))
      return fail(422, "없는 프로젝트입니다.");
    if (input.parent_id) {
      const parent = db.weeklyComments.find((c) => c.id === input.parent_id);
      if (!parent) return fail(422, "답글을 달 코멘트가 없습니다.");
      if (parent.parent_id) return fail(422, "답글에는 답글을 달 수 없습니다.");
    }
    const made: WeeklyComment = {
      id: `weekly-comment-${crypto.randomUUID().slice(0, 8)}`,
      week_start: weekStartOf(input.week_start),
      project_id: input.project_id,
      parent_id: input.parent_id ?? null,
      author_id: user.id,
      body,
      created_at: new Date().toISOString(),
    };
    db.weeklyComments.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.delete("/api/v1/weekly/comments/:id/", async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const comment = db.weeklyComments.find((c) => c.id === params.id);
    if (!comment) return fail(404, "코멘트를 찾을 수 없습니다.");
    if (comment.author_id !== user.id && user.role !== "admin")
      return fail(403, "쓴 사람이나 관리자만 지울 수 있습니다.");
    // 원 코멘트를 지우면 답글도 같이 지운다.
    db.weeklyComments = db.weeklyComments.filter(
      (c) => c.id !== comment.id && c.parent_id !== comment.id
    );
    return new HttpResponse(null, { status: 204 });
  }),
];
